import type { ReactNode } from 'react';
import { useState } from 'react';
import { Archive, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertBanner } from '@/components/AlertBanner';
import { labelClassName } from '@/components/ui/label';
import { Table, TableHead, TableBody, TableRow, TableHeadCell, TableCell } from '@/components/ui/table';
import { callReadAuditAsset } from '@/lib/functions';
import { useT } from '@/lib/i18n';
import type { Tone } from '@/lib/catalog';
import type { messages as MSG } from '@/lib/i18n.messages';

type TKey = keyof typeof MSG;
type TranslateFn = (key: TKey, vars?: Record<string, string | number>) => string;

/**
 * Traduit une valeur d'énumération du contrat (`database`, `ready`,
 * `internal`…) et retombe sur la valeur BRUTE quand le portail ne la connaît
 * pas : une énumération élargie par le toolkit reste lisible au lieu
 * d'afficher une clé i18n.
 *
 * La clé est construite au point d'appel (`'audit.catalog.kind.' + kind`) pour
 * que le préfixe reste un littéral : c'est ce qui rend l'usage repérable, à la
 * lecture comme pour `scripts/i18n_unused.py`.
 */
export function contractLabel(t: TranslateFn, key: string, raw: string | null | undefined): string {
  if (!raw) return '—';
  const out = t(key as TKey);
  return out === key ? raw : out;
}

/** Section titrée avec, au besoin, une phrase d'explication et un compteur. */
export function Section({ title, description, aside, children }: {
  title: string; description?: string; aside?: ReactNode; children: ReactNode;
}) {
  return (
    <Card className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-display text-lg text-ink-900 dark:text-white">{title}</h2>
        {aside && <div className="ml-auto flex flex-wrap items-center gap-2 text-xs text-ink-500">{aside}</div>}
      </div>
      {description && <p className="text-xs text-ink-500">{description}</p>}
      {children}
    </Card>
  );
}

/** Tableau simple : en-têtes traduits, message « aucune entrée » si vide. */
export function DataTable({ headers, rows, empty }: {
  headers: string[]; rows: ReactNode[][]; empty: string;
}) {
  if (rows.length === 0) return <div className="text-sm text-ink-500">{empty}</div>;
  return (
    <Table>
      <TableHead>
        <TableRow>{headers.map(h => <TableHeadCell key={h}>{h}</TableHeadCell>)}</TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={i}>
            {row.map((cell, j) => <TableCell key={j} className="align-top">{cell}</TableCell>)}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** Paire libellé / valeur pour les blocs de chiffres (dimensionnement, ingestion). */
export function Metric({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <div className={labelClassName()}>{label}</div>
      <div className="mt-1 font-display text-lg text-ink-900 dark:text-white">{value}</div>
      {sub && <div className="text-xs text-ink-500">{sub}</div>}
    </div>
  );
}

export function ToneBadge({ tone, children }: { tone: Tone; children: ReactNode }) {
  return <Badge variant={tone}>{children}</Badge>;
}

/** Liste courte rendue en puces ; `—` quand elle est vide. */
export function Bullets({ items }: { items: string[] }) {
  if (items.length === 0) return <>—</>;
  return (
    <ul className="list-inside list-disc space-y-0.5">
      {items.map((x, i) => <li key={i}>{x}</li>)}
    </ul>
  );
}

/**
 * Un audit antérieur au contrat 1.2 ne porte ni catalogue ni préconisations :
 * l'onglet explique pourquoi au lieu d'afficher une page vide.
 */
export function ContractTooOld({ title, schemaVersion }: { title: string; schemaVersion?: string | null }) {
  const { t } = useT();
  return (
    <AlertBanner variant="info" icon={<Archive className="h-4 w-4" />} title={title}>
      <div className="text-xs">
        {t('audit.catalog.unavailable.desc', {
          version: schemaVersion || t('audit.catalog.unavailable.legacy'),
        })}
      </div>
    </AlertBanner>
  );
}

/**
 * JSON d'un artefact 1.2 : inline quand il tient dans le document Firestore,
 * sinon chargé à la demande depuis Storage (lecture journalisée côté
 * fonctions, réservée aux administrateurs — `readAuditAsset`).
 */
export function useArtifactJson(auditId: string | undefined, module: { json?: unknown; jsonPath?: string } | undefined) {
  const [loaded, setLoaded] = useState<unknown>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inline = module?.json;
  const path = module?.jsonPath;

  const load = async () => {
    /* v8 ignore next -- garde de typage : le bouton n'existe que si les deux sont là */
    if (!auditId || !path) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await callReadAuditAsset({ auditId, path });
      setLoaded(JSON.parse(data.text));
    } catch (e) {
      setError((e as Error).message);
    } finally { setLoading(false); }
  };

  return { json: inline ?? loaded, path, loading, error, load, canLoad: !!auditId && !!path };
}

/** Bandeau « stocké à part » + bouton de chargement pour un artefact hors Firestore. */
export function ArtifactLoader({ path, label, loading, error, onLoad }: {
  path: string; label: string; loading: boolean; error: string | null; onLoad: () => void;
}) {
  const { t } = useT();
  return (
    <Card className="space-y-3">
      <div className="text-sm text-ink-500">{t('audit.catalog.stored_apart', { path })}</div>
      <Button variant="secondary" onClick={onLoad} disabled={loading}>
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {label}
      </Button>
      <div className="text-xs text-ink-500">{t('audit.module.asset.logged')}</div>
      {error && <div className="text-xs text-rose-600 dark:text-rose-400">{error}</div>}
    </Card>
  );
}
